<script>
import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConversationAPI from 'dashboard/api/conversations';
import messageAPI from 'dashboard/api/inbox/message';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'ForwardMessageModal',
  components: {
    Modal,
    NextButton,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      show: true,
      conversations: [],
      loading: false,
      forwarding: false,
      selectedConversationIds: new Set(),
    };
  },
  async mounted() {
    await this.fetchConversations();
  },
  methods: {
    async fetchConversations() {
      this.loading = true;
      try {
        const response = await ConversationAPI.get();
        this.conversations = response.data?.data?.payload || [];
      } catch (error) {
        console.error('Error fetching conversations:', error);
        this.conversations = [];
      } finally {
        this.loading = false;
      }
    },
    handleClose() {
      this.show = false;
      this.onClose();
    },
    toggleConversation(conversationId) {
      if (this.selectedConversationIds.has(conversationId)) {
        this.selectedConversationIds.delete(conversationId);
      } else {
        this.selectedConversationIds.add(conversationId);
      }
    },
    isSelected(conversationId) {
      return this.selectedConversationIds.has(conversationId);
    },
    handleSelectAll() {
      if (this.selectedConversationIds.size === this.conversations.length) {
        this.selectedConversationIds.clear();
      } else {
        this.selectedConversationIds = new Set(
          this.conversations.map(conv => conv.id)
        );
      }
    },
    async handleForward() {
      if (this.selectedConversationIds.size === 0) {
        return;
      }

      this.forwarding = true;
      try {
        const conversationId = this.message.conversation_id || this.message.conversationId;
        const messageId = this.message.id;
        const targetConversationIds = Array.from(this.selectedConversationIds);

        await messageAPI.forwardMessage(conversationId, messageId, targetConversationIds);
        useAlert(this.$t('CONVERSATION.FORWARD_MESSAGE.SUCCESS'));
        this.handleClose();
      } catch (error) {
        console.error('Error forwarding message:', error);
        const errorMessage =
          error?.response?.data?.error ||
          error?.message ||
          this.$t('CONVERSATION.FORWARD_MESSAGE.ERROR');
        useAlert(errorMessage);
      } finally {
        this.forwarding = false;
      }
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="handleClose">
    <div class="space-y-6 p-6">
      <!-- Header Section -->
      <div
        class="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm space-y-2"
      >
        <p class="text-xs font-semibold uppercase tracking-wide text-woot-500">
          {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD') }}
        </p>
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-xl font-semibold">
              {{ $t('CONVERSATION.FORWARD_MESSAGE.DESCRIPTION') }}
            </h3>
            <p class="text-sm text-slate-500">
              {{ $t('CONVERSATION.FORWARD_MESSAGE.SELECT_CONVERSATION') }}
            </p>
          </div>
          <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">
              {{ $t('CONVERSATION.FORWARD_MESSAGE.SELECTED_LABEL') }}
            </p>
            <p class="text-lg font-semibold text-slate-900">
              {{ selectedConversationIds.size }}/{{ conversations.length }}
            </p>
          </div>
        </div>
      </div>

      <!-- Conversations List Section -->
      <div class="space-y-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-xs uppercase tracking-wide text-slate-500">
              {{ $t('CONVERSATION.FORWARD_MESSAGE.CONVERSATIONS_LABEL') }}
            </p>
            <h4 class="text-lg font-semibold">
              {{ $t('CONVERSATION.FORWARD_MESSAGE.SELECT_CONVERSATION') }}
            </h4>
          </div>
          <NextButton
            v-if="conversations.length > 0"
            variant="ghost"
            color="slate"
            size="sm"
            @click="handleSelectAll"
          >
            {{
              selectedConversationIds.size === conversations.length
                ? $t('KANBAN.BULK_ACTIONS.DESELECT_ALL')
                : $t('KANBAN.BULK_ACTIONS.SELECT_ALL')
            }}
          </NextButton>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
          <span class="loading-spinner" />
        </div>

        <!-- Empty State -->
        <div
          v-else-if="conversations.length === 0"
          class="rounded-xl border border-dashed border-slate-200 bg-slate-50 py-12 text-center"
        >
          <p class="text-sm text-slate-500">
            {{ $t('CONVERSATION.FORWARD_MESSAGE.NO_CONVERSATIONS') }}
          </p>
        </div>

        <!-- Conversations List -->
        <div v-else class="max-h-96 overflow-y-auto space-y-2">
          <div
            v-for="conversation in conversations"
            :key="conversation.id"
            :class="[
              'flex cursor-pointer items-start gap-4 rounded-2xl border px-4 py-3 text-sm shadow-sm transition',
              isSelected(conversation.id)
                ? 'border-woot-200 bg-woot-25 hover:border-woot-300'
                : 'border-slate-100 hover:border-woot-200 hover:bg-woot-25'
            ]"
            @click="toggleConversation(conversation.id)"
          >
            <input
              type="checkbox"
              :checked="isSelected(conversation.id)"
              class="mt-1 h-4 w-4 rounded border-slate-300 text-woot-600 focus:ring-woot-500"
              @click.stop
            />
            <div class="flex-1">
              <p class="font-medium text-slate-900">
                {{ conversation.meta?.sender?.name || `Conversation #${conversation.id}` }}
              </p>
              <p v-if="conversation.meta?.channel" class="text-xs text-slate-500">
                {{ conversation.meta.channel }}
              </p>
              <p v-else class="text-xs text-slate-500">
                ID: {{ conversation.id }}
              </p>
            </div>
            <span
              v-if="isSelected(conversation.id)"
              class="inline-flex items-center rounded-full bg-woot-100 px-3 py-1 text-xs font-semibold text-woot-700"
            >
              {{ $t('CONVERSATION.FORWARD_MESSAGE.SELECTED') }}
            </span>
          </div>
        </div>
      </div>

      <!-- Summary Section -->
      <div class="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
        <p class="text-sm text-slate-600">
          {{
            $t('CONVERSATION.FORWARD_MESSAGE.SUMMARY', {
              count: selectedConversationIds.size,
              total: conversations.length,
            })
          }}
        </p>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-2">
        <NextButton
          variant="ghost"
          color="slate"
          size="sm"
          @click="handleClose"
        >
          {{ $t('CONVERSATION.FORWARD_MESSAGE.CANCEL') }}
        </NextButton>
        <NextButton
          variant="solid"
          color="blue"
          size="sm"
          :disabled="selectedConversationIds.size === 0 || forwarding"
          :loading="forwarding"
          @click="handleForward"
        >
          {{ $t('CONVERSATION.FORWARD_MESSAGE.FORWARD') }}
        </NextButton>
      </div>
    </div>
  </Modal>
</template>

